import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['shape', 'label', 'checkbox']
  readonly shapeTargets!: HTMLElement[]
  readonly labelTargets!: HTMLElement[]
  readonly checkboxTargets!: HTMLInputElement[]

  connect (): void {
    const shapeMap = new Map()
    const labelMap = new Map()
    const checkboxMap = new Map()
    this.shapeTargets.forEach(shape => shapeMap.set(shape.dataset.identifier, shape))
    this.labelTargets.forEach(label => labelMap.set(label.dataset.identifier, label))
    this.checkboxTargets.forEach(checkbox => checkboxMap.set(checkbox.dataset.identifier, checkbox))
    this.addShapeToShapeVisibilityListeners(checkboxMap)
    this.addCheckboxToShapeVisibilityListeners(shapeMap)
    this.addShapeToLabelHighlightListeners(labelMap)
    this.addLabelToShapeHighlightListeners(shapeMap)
  }

  private addShapeToShapeVisibilityListeners (checkboxMap: Map<string, HTMLInputElement>): void {
    this.shapeTargets.forEach(shape => {
      if (shape.dataset.identifier === undefined) return
      const checkbox = checkboxMap.get(shape.dataset.identifier)
      if (checkbox === undefined) return
      shape.addEventListener('click', () => {
        checkbox.checked = !checkbox.checked
        this.hideOrShowShapeBasedOnCheckbox(shape, checkbox)
        this.resetCursor()
      })
    })
  }

  private addCheckboxToShapeVisibilityListeners (shapeMap: Map<string, HTMLElement>): void {
    this.checkboxTargets.forEach(checkbox => {
      if (checkbox.dataset.identifier === undefined) return
      const shape = shapeMap.get(checkbox.dataset.identifier)
      if (shape === undefined) return
      checkbox.addEventListener('change', () => { this.hideOrShowShapeBasedOnCheckbox(shape, checkbox) })
    })
  }

  private addShapeToLabelHighlightListeners (labelMap: Map<string, HTMLElement>): void {
    this.shapeTargets.forEach(shape => {
      if (shape.dataset.identifier === undefined) return
      const label = labelMap.get(shape.dataset.identifier)
      if (label === undefined) return
      shape.addEventListener('mouseenter', () => { this.highlight(shape, label) })
      shape.addEventListener('mouseleave', () => { this.dehighlight(shape, label) })
    })
  }

  private addLabelToShapeHighlightListeners (shapeMap: Map<string, HTMLElement>): void {
    this.labelTargets.forEach(label => {
      if (label.dataset.identifier === undefined) return
      const shape = shapeMap.get(label.dataset.identifier)
      if (shape === undefined) return
      label.addEventListener('mouseenter', () => { this.highlight(shape, label) })
      label.addEventListener('mouseleave', () => { this.dehighlight(shape, label) })
    })
  }

  private hideOrShowShapeBasedOnCheckbox (shape: HTMLElement, checkbox: HTMLInputElement): void {
    const display = checkbox.checked ? 'initial' : 'none'
    shape.style.display = display
  }

  private highlight (shape: HTMLElement, label: HTMLElement): void {
    shape.classList.add('apexSensorScene-svg-shape-highlight')
    label.classList.add('apexSensorScene-shapeSelectionLabel-highlight')
  }

  private dehighlight (shape: HTMLElement, label: HTMLElement): void {
    label.classList.remove('apexSensorScene-shapeSelectionLabel-highlight')
    shape.classList.remove('apexSensorScene-svg-shape-highlight')
  }

  // Fixes glitch in FF. Without this, hiding a shape by clicking on it doesn't immediately dehighlight the label.
  // See https://bugzilla.mozilla.org/show_bug.cgi?id=2005828
  private resetCursor (): void {
    const originalStyle = document.body.style.cursor
    document.body.style.cursor = 'none'
    setTimeout(() => { document.body.style.cursor = originalStyle }, 0)
  }
}
